/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosError } from "axios";
import { getDataFetcher } from "../../api/ApiConfig";
import { CapFormData } from "../../api/types";
import { asyncDebounce } from "../../utils";
import { FieldValidationParams } from "../types";
import { fieldValidationCache } from "./fieldValidationCache";
import { validationException } from "./validator.utils";

export const validateCapFn = async (cap: string): Promise<CapFormData> => {
  const req = await getDataFetcher().get("/cities/" + cap);
  return req.data;
};

const debouncedValidatorFn = asyncDebounce(validateCapFn, 400);
const CONTROL_NAME = "cap";
const INVALID_CONTROL_MESSAGE = "Cap is not valid.";

const checkIsInvalidError = (cb: () => void, error = 0) => {
  if (error >= 400 && error < 500) {
    cb();
  }
};
export const validateCapField = async ({
  args,
  names,
  onValidated,
  onInvalid,
  onValidationStarted,
}: FieldValidationParams) => {
  return new Promise((resolve, reject) => {
    const [active, value, defs, schema] = args;
    if (!active) {
      return resolve(true);
    }
    const { instancePath } = schema;
    const paths = instancePath?.split("/");
    const name = paths[paths.length - 1];
    const deps = [name];
    const cacheName = `${name}${value}`;
    const { capProvinceFieldName = "province" } = defs;

    const checkValidated = (values: Record<string, unknown>) => {
      if (onValidated) {
        onValidated(names, name, CONTROL_NAME, deps, values);
      }
    };
    const checkInvalid = (errorCode = 0) => {
      if (onInvalid) {
        onInvalid(names, name, CONTROL_NAME, errorCode, [capProvinceFieldName]);
      }
    };
    if (onValidationStarted) {
      onValidationStarted(CONTROL_NAME, name);
    }
    if (value.length !== 5) {
      checkInvalid(0);
      return reject(
        validationException(
          "minLength",
          "At least 5 numeric characters are required.",
        ),
      );
    }
    // if the validation failed previously and was cached as invalid just reject.
    const isCachedAsInvalid =
      fieldValidationCache.isFieldInvalidValid(cacheName);
    if (isCachedAsInvalid) {
      checkInvalid(0);
      return reject(validationException(CONTROL_NAME, INVALID_CONTROL_MESSAGE));
    }
    const shouldRevalidate = fieldValidationCache.shouldRevalidate(
      cacheName,
      value,
      [],
    );

    if (!shouldRevalidate) {
      const cachedValue = fieldValidationCache.fields.get(cacheName)
        ?.result as Record<string, unknown>;
      if (cachedValue) {
        const values = {
          [capProvinceFieldName]: cachedValue.country,
        };
        checkValidated(values);
        return resolve(cachedValue);
      }
    }

    debouncedValidatorFn(value)
      .then(res => {
        if (
          !res?.cap ||
          !Array.isArray(res.cities) ||
          !res.country ||
          res.cities.length === 0
        ) {
          checkInvalid(0);
          fieldValidationCache.addInvalidItem(cacheName, {
            deps: [],
            value,
          });
          return reject(
            validationException(CONTROL_NAME, INVALID_CONTROL_MESSAGE),
          );
        }
        const values = {
          [capProvinceFieldName]: res.country,
        };
        checkValidated(values);
        // no dependencies for caching since they are not editable (province).
        fieldValidationCache.add(cacheName, {
          deps: [],
          value,
          result: res,
        });
        return resolve(true);
      })
      .catch(e => {
        const err = e as AxiosError;
        checkIsInvalidError(() => {
          fieldValidationCache.addInvalidItem(cacheName, {
            deps: [],
            value,
          });
        }, err?.response?.status);
        checkInvalid(err.response?.status);
        return reject(
          validationException(CONTROL_NAME, INVALID_CONTROL_MESSAGE),
        );
      });
  });
};
